import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import Select from "../../components/form/Select";
import AdminFormPage from "../../components/admin/AdminFormPage";
import { adminFormGridClass } from "../../components/admin/adminStyles";
import { SelectOption } from "../../types/select";

interface RolePermissionMapping {
  id: string;
  roleId: string;
  permissionId: string;
}

interface Role {
  id: string;
  roleName: string;
}

interface Permission {
  id: string;
  permissionName: string;
}

export default function RolePermissionMappingForm() {
  const { mappingId } = useParams<{ mappingId?: string }>();
  const navigate = useNavigate();

  const [mapping, setMapping] = useState<RolePermissionMapping>({
    id: mappingId || "",
    roleId: "",
    permissionId: "",
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        axiosInstance.get("/Role/getall", { params: { pageSize: 100 } }),
        axiosInstance.get("/Permission/getall", { params: { pageSize: 100 } }),
      ]);

      setRoles(rolesRes.data?.roles || []);
      setPermissions(permissionsRes.data?.permissions || []);

      if (mappingId) {
        const mappingRes = await axiosInstance.get(`/RolePermissionMapping/get/${mappingId}`);
        setMapping({ ...mappingRes.data, id: mappingId });
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [mappingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!mapping.roleId || !mapping.permissionId) {
      setError("Role and Permission are required.");
      return;
    }

    setSaving(true);
    try {
      if (mappingId) {
        await axiosInstance.put(`/RolePermissionMapping/update/${mappingId}`, {
          ...mapping,
          id: mappingId,
        });
      } else {
        await axiosInstance.post(`/RolePermissionMapping/add`, mapping);
      }
      navigate("/role-permission-mapping");
    } catch (err) {
      setError("Error saving mapping. Please try again.");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminFormPage
      pageTitle={mappingId ? "Edit Role Permission Mapping" : "Add Role Permission Mapping"}
      description="Assign permissions to admin roles"
      error={error}
      loading={loading}
      onSubmit={handleSubmit}
      listRoute="/role-permission-mapping"
      saving={saving}
      saveLabel={mappingId ? "Update Mapping" : "Add Mapping"}
    >
      <div className={adminFormGridClass}>
        <Select
          label="Role"
          name="roleId"
          value={mapping.roleId}
          onChange={(value: string) => setMapping((prev) => ({ ...prev, roleId: value }))}
          placeholder="Select Role"
          options={roles.map(
            (role): SelectOption => ({
              value: role.id,
              label: role.roleName,
            })
          )}
        />
        <Select
          label="Permission"
          name="permissionId"
          value={mapping.permissionId}
          onChange={(value: string) => setMapping((prev) => ({ ...prev, permissionId: value }))}
          placeholder="Select Permission"
          options={permissions.map(
            (permission): SelectOption => ({
              value: permission.id,
              label: permission.permissionName,
            })
          )}
        />
      </div>
    </AdminFormPage>
  );
}
